import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "./CustomModal";
import AuthInput from "../Input/AuthInput";
import AuthTextArea from "../Input/AuthTextArea";
import AddingLightLoader from "../Loaders/AddingLightLoader";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import { CreateLoanApi } from "../../ApiRequests";
import AuthBtn from "../Buttons/AuthBtn";
import { fetchLoanLog, fetchLoans } from "../../store/Slices/LoanSlice";
import LoanPartySelector from "../Selector/LoanPartySelector";
import PaymentAccountSelector from "../Selector/PaymentAccountSelector";
import { fetchAccounts } from "../../store/Slices/AccountSlice";
import { LOAN_TYPE_GIVEN } from "../../utils/LoanConverter";
import { toLocalYmd } from "../../utils/dateTime";

const userData = JSON.parse(localStorage.getItem("user")) || {};
const { branchId = "" } = userData;

const AddLoanModal = ({ Open, setOpen, existingParty = null }) => {
  const [selectedParty, setSelectedParty] = useState("");
  const [partyName, setPartyName] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [marka, setMarka] = useState("");
  const [bichakNo, setBichakNo] = useState("");
  const [desc, setDesc] = useState("");
  const [curDate, setCurDate] = useState(
    toLocalYmd()
  );
  const [dueDate, setDueDate] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const AccountState = useSelector((state) => state.AccountState);

  useEffect(() => {
    if (!Open) return;
    dispatch(fetchAccounts());
    dispatch(fetchLoans());
  }, [Open, dispatch]);

  useEffect(() => {
    if (!Open) return;
    if (existingParty?._id) {
      setSelectedParty(existingParty);
      setPartyName(existingParty.partyName || "");
      setPhone(existingParty.phone || "");
    } else {
      setSelectedParty("");
      setPartyName("");
      setPhone("");
    }
  }, [Open, existingParty]);

  useEffect(() => {
    if (!Open || selectedAccount?._id) return;
    const accounts = Array.isArray(AccountState.data) ? AccountState.data : [];
    const cash =
      accounts.find(
        (a) => String(a.account_name || "").toUpperCase() === "CASH"
      ) || accounts[0];
    if (cash) setSelectedAccount(cash);
  }, [Open, selectedAccount, AccountState.data]);

  useEffect(() => {
    if (selectedParty?._id) {
      setPartyName(selectedParty.partyName || "");
      setPhone(selectedParty.phone || "");
    }
  }, [selectedParty]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const name = selectedParty?._id
      ? selectedParty.partyName
      : partyName.trim();

    if (!name) WarningToast("Enter or select party name");
    else if (!amount || isNaN(amount)) WarningToast("Enter valid amount");
    else if (!selectedAccount?._id) WarningToast("Select account");
    else if (!marka.trim()) WarningToast("Enter marka");
    else {
      try {
        const response = await CreateLoanApi({
          loanAccountId: selectedParty?._id || undefined,
          partyName: name,
          phone: phone.trim(),
          loanType: LOAN_TYPE_GIVEN,
          amount: Number(amount),
          accountId: selectedAccount._id,
          branchId,
          date: curDate,
          dueDate: dueDate || undefined,
          marka: marka.trim(),
          bichak_no: bichakNo.trim() || undefined,
          desc,
        });
        if (response.data.success) {
          setOpen(false);
          const portalUser = response.data.data?.payload?.portalUser;
          if (portalUser?.isNew) {
            SuccessToast(
              `Portal created — Email: ${portalUser.email} · Password: ${portalUser.temporaryPassword}`
            );
          } else {
            SuccessToast(
              selectedParty?._id
                ? "Additional loan given to party"
                : "Loan account created"
            );
          }
          dispatch(fetchLoans());
          dispatch(fetchLoanLog());
          dispatch(fetchAccounts());
        } else {
          ErrorToast(response.data.error?.msg);
        }
      } catch (err) {
        ErrorToast(err.response?.data?.error?.msg || "Failed to add loan");
      }
    }
    setLoading(false);
  };

  const isExisting = Boolean(selectedParty?._id || existingParty?._id);

  return (
    <CustomModal open={Open} setOpen={setOpen}>
      <div className="pb-8">
        <h1 className="w-full text-center font-[700] text-3xl py-8">
          {isExisting ? "Give more loan" : "Give loan"}
        </h1>
        <div className="flex flex-col gap-y-4 px-10 max696:max-h-[70vh] max696:overflow-y-auto">
          {!existingParty?._id && (
            <LoanPartySelector
              selectedParty={selectedParty}
              setSelectedParty={setSelectedParty}
            />
          )}
          {!selectedParty?._id && !existingParty?._id && (
            <>
              <AuthInput
                label="New party name"
                placeholder="Person or business name"
                Value={partyName}
                setValue={setPartyName}
              />
              <AuthInput
                label="Phone or email (portal login)"
                placeholder="Optional — email used for party login"
                Value={phone}
                setValue={setPhone}
              />
            </>
          )}
          <PaymentAccountSelector
            label="Pay from account"
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
          />
          <AuthInput
            label="Marka"
            placeholder="Marka"
            Value={marka}
            setValue={setMarka}
          />
          <AuthInput
            label="Bichak No (optional)"
            placeholder="Bichak number"
            Value={bichakNo}
            setValue={setBichakNo}
          />
          <AuthInput
            label="Amount"
            Type="number"
            Value={amount}
            setValue={setAmount}
          />
          <AuthInput label="Date" Type="date" Value={curDate} setValue={setCurDate} />
          {!isExisting && (
            <AuthInput
              label="Due date (optional)"
              Type="date"
              Value={dueDate}
              setValue={setDueDate}
            />
          )}
          <AuthTextArea label="Notes" Value={desc} setValue={setDesc} />
        </div>
        <div className="flex justify-center gap-x-2 mt-5">
          {loading ? (
            <AddingLightLoader />
          ) : (
            <>
              <AuthBtn
                title={isExisting ? "Give loan" : "Create account"}
                onSubmit={onSubmit}
                type={1}
              />
              <AuthBtn title="Cancel" onSubmit={() => setOpen(false)} type={2} />
            </>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default AddLoanModal;

